import clsx from 'clsx';
import React, { useMemo } from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import PageNavigator from '@/src/components/project/Inbox2/Elements/PageNavigator/PageNavigator';
import { getFileEntryToUpdateStatus } from '@/src/components/project/Inbox2/models/FileEntry';
import { EmailFileEntry, FileEntry } from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';

import UpdateFileStatus from '../../../FileEntryViewer/components/UpdateFileStatus/UpdateFileStatus';
import { useFileEntryViewerSidebarActions } from '../../../FileEntryViewer/store/fileEntryViewer.sidebarActions.store';
import TagsManager from '@/src/components/project/Tags/TagsManager';
import { useParams } from 'react-router';
import LongTextByWidth from '@/src/components/common/Elements/LongText/LongTextByWidth';

type HeaderProps = {
  fileEntry: FileEntry;
  files: EmailFileEntry[];
  totalCount: number;
  isSentEmailType: boolean;
  onSetEmailFileEntry: (emailFileEntry: any) => void;
  onFetchNextPage: (index: number) => void;
  onCloseFileViewer: () => void;
};

const Header = ({
  fileEntry,
  files,
  totalCount,
  isSentEmailType,
  onSetEmailFileEntry,
  onFetchNextPage,
  onCloseFileViewer,
}: HeaderProps) => {
  const { sidebarActions } = useFileEntryViewerSidebarActions();
  const { isTrash } = useInboxSearchParams();
  const updateStatusMutation = sidebarActions.updateStatusMutation;

  const rowId = fileEntry.file.id ? String(fileEntry.file.id) : 'header-row';
  const { projectId } = useParams<{ projectId: string }>();

  const assignedTags = fileEntry.tags ?? [];

  const currentIndex = useMemo(() => {
    return files.findIndex((f) => f.file.id === fileEntry.id);
  }, [files, fileEntry.id]);

  const handleClickLeft = () => {
    onSetEmailFileEntry(files[currentIndex - 1]);
  };

  const handleClickRight = async () => {
    const nextIndex = currentIndex + 1;
    const nextFile = files[nextIndex];
    if (nextFile) {
      onSetEmailFileEntry(files[nextIndex]);
    } else {
      onFetchNextPage(nextIndex);
    }
  };

  const handleCloseFileViewer = () => {
    onSetEmailFileEntry(null);
    onCloseFileViewer?.();
  };

  return (
    <div
      className={clsx(
        'tw-flex tw-items-center tw-justify-between',
        'tw-shadow-none',
        'tw-p-[10px] tw-mb-[10px]'
      )}
    >
      <Button
        id={''}
        className={clsx(
          'tw-group',
          'tw-flex tw-items-center',
          'tw-text-cfo-black tw-font-brandonTextMedium',
          'hover:tw-text-cfo-blue-700'
        )}
        onClick={handleCloseFileViewer}
      >
        <Icon icon={ICONS.close} className={clsx('tw-mr-[5px] tw-text-[18px]')} />
        <LongTextByWidth
          className={clsx(
            'tw-flex tw-items-center',
            'tw-text-cfo-black tw-leading-normal tw-font-brandonTextMedium'
          )}
          tooltipClassName="tw-max-w-[500px] tw-w-auto tw-text-center tw-whitespace-normal tw-break-words"
          id={`document_subject`}
          maxLines={1}
          maxWidth={120}
          value={fileEntry.file.name}
          hasTooltip
        />
      </Button>
      <div className="tw-flex tw-justify-center tw-relative tw-max-w-[200px]">
        <div className="tw-self-center tw-flex tw-flex-row tw-mr-[8px]">
          <TagsManager
            rowId={rowId}
            isFilesTable={false}
            projectId={projectId}
            inboxDocumentId={fileEntry.id}
            isLibrary={false}
            assignedTags={assignedTags}
          />
        </div>
      </div>
      <div className={'tw-flex tw-items-center'}>
        {!isTrash && !isSentEmailType && (
          <UpdateFileStatus
            fileEntry={getFileEntryToUpdateStatus(fileEntry)}
            updateStatusMutation={updateStatusMutation}
          />
        )}
        <PageNavigator
          currentPage={currentIndex + 1}
          totalCount={totalCount}
          onClickLeft={handleClickLeft}
          onClickRight={handleClickRight}
        />
      </div>
    </div>
  );
};

export default Header;
